import { useCallback, useEffect, useRef } from 'react';

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

/**
 * Hook to load reCAPTCHA v3 and execute tokens.
 * Returns null token if no site key is configured (dev mode).
 */
export function useRecaptcha() {
  const loaded = useRef(false);

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || loaded.current) return;

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    document.head.appendChild(script);
    loaded.current = true;

    return () => {
      // Cleanup not strictly needed for recaptcha, but remove badge on unmount
    };
  }, []);

  const execute = useCallback(async (action: string): Promise<string | null> => {
    if (!RECAPTCHA_SITE_KEY) return null;

    try {
      const grecaptcha = (window as any).grecaptcha;
      if (!grecaptcha) return null;

      return await new Promise<string>((resolve) => {
        grecaptcha.ready(() => {
          grecaptcha.execute(RECAPTCHA_SITE_KEY, { action }).then(resolve);
        });
      });
    } catch {
      console.warn('reCAPTCHA execution failed');
      return null;
    }
  }, []);

  return { execute, enabled: !!RECAPTCHA_SITE_KEY };
}
